import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { mergeRegister } from "@lexical/utils";
import {
    COMMAND_PRIORITY_HIGH,
    PASTE_COMMAND,
    $getSelection,
    $isRangeSelection,
} from "lexical";
import { TOGGLE_LINK_COMMAND } from "@lexical/link";
import { useEffect } from "react";

// URL validation regex - more comprehensive than the AutoLink one
const URL_REGEX = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/i;
const SIMPLE_URL_REGEX = /^(https?:\/\/|www\.)/i;
const DOMAIN_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$/;

/**
 * Check if text looks like a URL
 */
function isURL(text) {
    if (!text || typeof text !== 'string') return false;

    // Remove whitespace
    const trimmed = text.trim();

    // Don't treat single words as URLs
    if (!trimmed.includes('.') && !trimmed.includes('/')) {
        return false;
    }

    // Quick check for common URL patterns
    if (SIMPLE_URL_REGEX.test(trimmed)) {
        return true;
    }

    // Check for domain-like patterns
    if (DOMAIN_REGEX.test(trimmed)) {
        return true;
    }

    // More thorough check
    return URL_REGEX.test(trimmed);
}

/**
 * Normalize URL by adding protocol if missing
 */
function normalizeURL(url) {
    if (!url) return url;
    
    const trimmed = url.trim();
    
    // If it already has a protocol, return as is
    if (/^https?:\/\//i.test(trimmed)) {
        return trimmed;
    }
    
    // If it starts with www., add https://
    if (/^www\./i.test(trimmed)) {
        return `https://${trimmed}`;
    }
    
    // If it looks like a domain, add https://
    if (URL_REGEX.test(trimmed)) {
        return `https://${trimmed}`;
    }
    
    return trimmed;
}

/**
 * Enhanced Link Plugin that provides:
 * 1. Auto-link creation when pasting URLs over selected text
 * 2. Smart URL detection and normalization
 */
export default function EnhancedLinkPlugin() {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        return mergeRegister(
            editor.registerCommand(
                PASTE_COMMAND,
                (event) => {
                    const selection = $getSelection();
                    
                    // Only handle if we have a range selection (text selected)
                    if (!$isRangeSelection(selection) || selection.isCollapsed()) {
                        return false;
                    }

                    const clipboardData = event.clipboardData || window.clipboardData;
                    if (!clipboardData) return false;

                    const pastedText = clipboardData.getData('text/plain');

                    // Check if pasted text is a URL
                    if (isURL(pastedText)) {
                        event.preventDefault();

                        // Normalize the URL
                        const normalizedURL = normalizeURL(pastedText);

                        // Create link with the selected text using editor.update
                        editor.update(() => {
                            const currentSelection = $getSelection();
                            if ($isRangeSelection(currentSelection) && !currentSelection.isCollapsed()) {
                                // Apply the link to the current selection
                                editor.dispatchCommand(TOGGLE_LINK_COMMAND, normalizedURL);
                            } else {
                                console.log('EnhancedLinkPlugin: No valid selection found');
                            }
                        });

                        return true;
                    }

                    return false;
                },
                COMMAND_PRIORITY_HIGH
            )
        );
    }, [editor]);

    return null;
}
